// Thin wrapper over the Team Tools remote config endpoints.
//
// The keys, their shapes and what is allowed in them all live on the server,
// in gameapi's registry. Nothing here knows what a key means; it carries JSON
// and shows back whatever the registry says when it refuses.

const BASE = '/team-tools/api/remote-config'

export const CLIENT = 1
export const SERVER = 2

export type ConfigKey = {
    key: string
    /** 1 client, 2 game server, null for a key the registry has dropped. */
    audience: number | null
    audience_label: string
    help: string
    /** False when nobody has ever given this key a value: the game falls back
     *  to whatever is compiled into the build. */
    exists: boolean
    enabled: boolean
    value: unknown
    notes: string
    updated_at: string | null
    updated_by: string | null
    /** Stored, but would not survive today's rules, so it is not delivered. */
    deliverable_error: string | null
    orphan?: boolean
}

export type FleetRow = {
    id: number
    name: string | null
    slot: string | null
    region: string
    echoed: number | null
    /** applied · pending · behind · never */
    state: string
}

export type ConfigChange = {
    id: number
    action: number
    action_label: string
    key: string
    old_value: unknown
    new_value: unknown
    revision: number
    by: string
    at: string
}

const csrf = () =>
    document.cookie
        .split('; ')
        .find((c) => c.startsWith('csrftoken='))
        ?.split('=')[1] ?? ''

async function send(path: string, method: string, body?: unknown) {
    const response = await fetch(`${BASE}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: body === undefined ? undefined : JSON.stringify(body),
    })
    let data: any = {}
    try {
        data = await response.json()
    } catch {
        // A 403 from the permission mixin carries a body; a 500 may not.
    }
    if (!response.ok) {
        throw new Error(data.error || `${method} ${path} failed (${response.status})`)
    }
    return data
}

export const getConfig = (): Promise<{
    revision: number
    changed_at: string | null
    keys: ConfigKey[]
    fleet: FleetRow[]
    now: string
}> => send('', 'GET')

export const saveKey = (
    key: string,
    body: { value?: unknown; enabled?: boolean; notes?: string }
): Promise<{ key: string; revision: number; entry?: ConfigKey; unchanged?: boolean }> => send(`/${key}`, 'POST', body)

export const deleteKey = (key: string) => send(`/${key}`, 'DELETE')

export const getHistory = (key?: string): Promise<{ total: number; changes: ConfigChange[] }> =>
    send(`/history${key ? `?key=${encodeURIComponent(key)}` : ''}`, 'GET')
